'use client'

import { useCallback, useMemo, useState } from 'react'
import { useAuth } from '@/store/hooks'
import PaginationControls from '@/components/pagination/PaginationControls'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import {
    getMedicalReportsByCompany,
    MedicalReport,
    MEDICAL_REPORT_TYPE_LABELS,
    MEDICAL_REPORT_TYPE_COLORS,
} from '@/lib/api/medical-reports/medical-report-api'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdSearch,
    MdClose,
    MdArrowForward,
    MdAdd,
    MdArticle,
    MdBusiness,
    MdPerson,
    MdAccessTime,
    MdVerified,
} from 'react-icons/md'

export default function ReportsPage() {
    const { user, isPartner, isEmployee } = useAuth()
    const hidePartnerSelector = isPartner || (isEmployee && !!user?.partnerId)
    const navigate = useNavigate()
    const { canCreate } = useResourcePermissions('bookings', 'reports')
    const [showSearch, setShowSearch] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [partnerFilter, setPartnerFilter] = useState<number | null>(null)
    const [urlPage, setUrlPage] = useUrlPage()

    const debouncedSearch = useDebounce(searchTerm, 300)

    const filters = useMemo(
        () => ({
            partnerId: partnerFilter ?? undefined,
            search: debouncedSearch.trim() || undefined,
        }),
        [partnerFilter, debouncedSearch],
    )

    const fetchFn = useCallback(
        (params: typeof filters & { page: number; limit: number }) => {
            return getMedicalReportsByCompany(user!.companyId!, params)
        },
        [user?.companyId],
    )

    const {
        items: displayedReports,
        loading,
        page,
        limit,
        total,
        setPage,
        reload,
    } = usePaginatedPage<MedicalReport, typeof filters>({
        fetchFn,
        filters,
        limit: 15,
        enabled: !!user?.companyId,
        page: urlPage,
        onPageChange: setUrlPage,
    })

    const handleClearSearch = () => {
        setSearchTerm('')
    }

    if (loading && displayedReports.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando laudos...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                        <MdArticle className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Laudos Médicos
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total}{' '}
                            {total === 1 ? 'laudo' : 'laudos'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {showSearch ? (
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                placeholder="Buscar laudos..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="input input-bordered input-sm w-64"
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    setShowSearch(false)
                                    handleClearSearch()
                                }}
                                className="btn btn-sm btn-ghost"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        </div>
                    ) : (
                        <>
                            <button
                                onClick={() => setShowSearch(true)}
                                className="btn btn-sm btn-ghost gap-2"
                            >
                                <MdSearch className="text-lg" />
                                <span className="hidden sm:inline">Buscar</span>
                            </button>
                            {canCreate && (
                                <button
                                    onClick={() => navigate('/bookings/reports/new')}
                                    className="btn btn-secondary gap-2"
                                >
                                    <MdAdd className="text-lg" />
                                    <span className="hidden sm:inline">
                                        Novo Laudo
                                    </span>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {!hidePartnerSelector && (
                <div className="flex gap-3 flex-wrap items-end">
                    <PartnerSelector
                        value={partnerFilter}
                        onChange={(id) => setPartnerFilter(id)}
                        label="Parceiro"
                        placeholder="Todos"
                        className="!w-48"
                    />
                </div>
            )}

            {displayedReports.length === 0 && !loading ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-12 text-center">
                    <MdArticle className="text-6xl text-base-content/20 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-base-content mb-2">
                        Nenhum laudo encontrado
                    </h3>
                    <p className="text-base-content/60 mb-6">
                        Comece adicionando seu primeiro laudo
                    </p>
                    {canCreate && (
                        <button
                            onClick={() => navigate('/bookings/reports/new')}
                            className="btn btn-secondary gap-2"
                        >
                            <MdAdd className="text-lg" />
                            Novo Laudo
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {displayedReports.map(report => (
                            <div
                                key={report.id}
                                onClick={() =>
                                    navigate(`/bookings/reports/${report.id}`)
                                }
                                className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-5 hover:shadow-md transition-shadow cursor-pointer"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span
                                                className={`badge ${MEDICAL_REPORT_TYPE_COLORS[
                                                    report.type
                                                ]
                                                    }`}
                                            >
                                                {
                                                    MEDICAL_REPORT_TYPE_LABELS[
                                                    report.type
                                                    ]
                                                }
                                            </span>
                                            {report.signedAt && (
                                                <MdVerified className="text-success" />
                                            )}
                                        </div>
                                        <h3 className="text-lg font-semibold text-base-content mb-1">
                                            {report.title}
                                        </h3>
                                        {report.client && (
                                            <div className="flex items-center gap-2 text-sm text-base-content/70">
                                                <MdPerson className="text-base" />
                                                <span className="truncate">
                                                    {report.client.name}
                                                </span>
                                            </div>
                                        )}
                                        {report.partner && (
                                            <div className="flex items-center gap-2 text-sm text-base-content/60">
                                                <MdBusiness className="text-base" />
                                                <span className="truncate">
                                                    {report.partner.name || report.partner.tradingName}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <MdArrowForward className="text-base-content/40 flex-shrink-0 ml-2" />
                                </div>

                                {report.summary && (
                                    <p className="text-sm text-base-content/60 line-clamp-2 mb-3">
                                        {report.summary}
                                    </p>
                                )}

                                <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                    <div className="flex items-center gap-2 text-xs text-base-content/60">
                                        <MdAccessTime className="text-sm" />
                                        <span>
                                            {format(
                                                new Date(report.reportDate),
                                                'dd/MM/yyyy',
                                                { locale: ptBR }
                                            )}
                                        </span>
                                    </div>
                                    {report.professional && (
                                        <span className="badge badge-sm badge-ghost">
                                            {report.professional.name.split(
                                                ' '
                                            )[0]}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="laudo"
                        />
                    )}
                </>
            )}
        </div>
    )
}
